/**
 * Canvas-rendered signs: text drawn once to a canvas texture and shown on an unlit plane.
 * Textures are capped at 1024 px wide and never redrawn per frame.
 */
import * as THREE from 'three';

export interface SignLine {
  text: string;
  size?: number; // px at 1024 wide
  weight?: number;
  colour?: string;
  letterSpacing?: number;
}

export interface SignOptions {
  lines: SignLine[];
  /** World size in metres. */
  width: number;
  height: number;
  background?: string;
  /** Background opacity; 0 gives transparent signs on glass. */
  backgroundOpacity?: number;
  align?: 'left' | 'center';
  padding?: number;
  /** Accent bar colour drawn on the left edge. */
  accent?: string;
  radius?: number;
}

const FONT = '"Inter", "Segoe UI", Roboto, system-ui, sans-serif';

export function makeSignTexture(opts: SignOptions): THREE.CanvasTexture {
  const pxWidth = 1024;
  const pxHeight = Math.max(64, Math.min(1024, Math.round((pxWidth * opts.height) / opts.width)));
  const canvas = document.createElement('canvas');
  canvas.width = pxWidth;
  canvas.height = pxHeight;
  const ctx = canvas.getContext('2d')!;
  const pad = opts.padding ?? 48;
  const radius = opts.radius ?? 24;
  ctx.clearRect(0, 0, pxWidth, pxHeight);
  if ((opts.backgroundOpacity ?? 1) > 0) {
    ctx.globalAlpha = opts.backgroundOpacity ?? 1;
    ctx.fillStyle = opts.background ?? '#121822';
    roundRect(ctx, 0, 0, pxWidth, pxHeight, radius);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  if (opts.accent) {
    ctx.fillStyle = opts.accent;
    roundRect(ctx, 0, 0, 14, pxHeight, 6);
    ctx.fill();
  }
  const total = opts.lines.reduce((sum, l) => sum + (l.size ?? 64) * 1.25, 0);
  let y = (pxHeight - total) / 2;
  const align = opts.align ?? 'left';
  ctx.textBaseline = 'top';
  for (const line of opts.lines) {
    const size = line.size ?? 64;
    ctx.font = `${line.weight ?? 500} ${size}px ${FONT}`;
    ctx.fillStyle = line.colour ?? '#f2f6fb';
    ctx.textAlign = align;
    if ('letterSpacing' in ctx)
      (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
        `${line.letterSpacing ?? 0}px`;
    const x = align === 'center' ? pxWidth / 2 : pad + (opts.accent ? 20 : 0);
    ctx.fillText(line.text, x, y, pxWidth - pad * 2);
    y += size * 1.25;
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  return texture;
}

export function makeSign(opts: SignOptions): THREE.Mesh {
  const texture = makeSignTexture(opts);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    toneMapped: false,
    depthWrite: (opts.backgroundOpacity ?? 1) > 0.5,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(opts.width, opts.height), material);
  mesh.name = 'sign';
  return mesh;
}

/** Re-renders an existing sign in place (data change only, never per frame). */
export function updateSign(mesh: THREE.Mesh, opts: SignOptions): void {
  const material = mesh.material as THREE.MeshBasicMaterial;
  material.map?.dispose();
  material.map = makeSignTexture(opts);
  material.needsUpdate = true;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
